import { useState } from 'react';
import type { Engine } from '../engine.js';
import { Section, Slider, Toggle } from './controls.js';

export interface ExportPanelProps {
  engine: Engine;
  ready: boolean;
}

export function ExportPanel({ engine, ready }: ExportPanelProps) {
  const [trim, setTrim] = useState(true);
  const [colors, setColors] = useState(0);
  const [dither, setDither] = useState(0);
  const [maxEdge, setMaxEdge] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const exported = await engine.workbench.export({
        trim,
        trimPadding: 0,
        ...(maxEdge > 0 ? { maxEdge } : {}),
        palette: { colors, dither, linear: true },
        type: 'image/png',
      });

      const url = URL.createObjectURL(exported.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `design-${exported.width}x${exported.height}.png`;
      link.click();
      // Revoke on the next task so the download has picked the blob up.
      setTimeout(() => URL.revokeObjectURL(url), 10_000);

      setResult(
        `${exported.width} x ${exported.height}, ${(exported.blob.size / 1024 / 1024).toFixed(2)} MB` +
          (exported.palette ? `, ${exported.palette.length} colours` : ''),
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section title="Export" subtitle="Transparent PNG, ready for a print provider.">
      <Toggle
        label="Trim transparent margin"
        checked={trim}
        onChange={setTrim}
        hint="Print services scale the file into the print area, so a margin shrinks the design."
      />
      <Slider
        label="Limit colours"
        value={colors}
        min={0}
        max={32}
        step={1}
        onChange={setColors}
        format={(value) => (value === 0 ? 'off' : `${value} inks`)}
        hint="Quantises to a palette - useful for screen printing and for killing JPEG ringing."
      />
      {colors > 0 ? (
        <Slider
          label="Dither"
          value={dither}
          min={0}
          max={0.2}
          step={0.01}
          onChange={setDither}
          format={(value) => (value === 0 ? 'off' : value.toFixed(2))}
        />
      ) : null}
      <Slider
        label="Max edge"
        value={maxEdge}
        min={0}
        max={8192}
        step={256}
        onChange={setMaxEdge}
        format={(value) => (value === 0 ? 'full size' : `${value}px`)}
      />
      <button
        type="button"
        className="button button--primary"
        disabled={!ready || busy}
        onClick={() => void run()}
      >
        {busy ? 'Encoding...' : 'Export PNG'}
      </button>
      {result ? <p className="ok">{result}</p> : null}
      {error ? <p className="error">{error}</p> : null}
    </Section>
  );
}
